import { UseQueryResult } from '@tanstack/react-query';
import { currentUserStore } from '../../../CurrentUserStore';
import { findLink } from '../../../LinkyLinky';
import { Link } from '../../../Resource/Types';
import { useQueryWithDecoder } from '../../../UseQueryWithDecoder';
import { interpolateLink } from '../Api';
import { connectionResourceDecoder, ssoConfigurationsResourceDecoder } from '../Decoders';
import { ConnectionResource } from '../Types';

// The connection detail URL comes from the hub resource's templated
// 'sso-configuration' link, so the hub is fetched (or read from cache) first.
export const useConnectionQuery = (
  id: string,
): UseQueryResult<ConnectionResource | undefined, Error> => {
  const hub = useQueryWithDecoder(
    'sso-configurations',
    currentUserStore.links,
    ssoConfigurationsResourceDecoder,
  );

  const detailLinks: ReadonlyArray<Link> = hub.data
    ? findLink('sso-configuration', hub.data.links)
        .map(interpolateLink('{sso_configuration_id}', id))
        .map((link) => [link])
        .getOrElseValue([])
    : [];

  return useQueryWithDecoder(
    'sso-configuration',
    detailLinks,
    connectionResourceDecoder,
    {
      enabled: detailLinks.length > 0,
      // While the wizard waits on an out-of-band step (the admin testing in
      // another tab), poll so the page advances on its own.
      refetchInterval: (query) => {
        const status = query.state.data && connectionStatusOf(query.state.data);
        return status === 'attributes_check' || status === 'verifying' ? 5000 : false;
      },
    },
    [id],
  );
};

const connectionStatusOf = (raw: string): string | undefined =>
  connectionResourceDecoder
    .decodeJson(raw)
    .map((resource) => resource.payload.status)
    .cata<string | undefined>({
      Ok: (status) => status,
      Err: () => undefined,
    });
